import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type TourStatus = 'idle' | 'active' | 'skipped' | 'completed';

interface TourState {
  status: TourStatus;
  currentStepIndex: number;
  /** Bumped when reset() is called so listeners can re-trigger. */
  resetNonce: number;
  /** Users this browser has already shown the intro tour to (LIL-355: first visit only). */
  seenUserIds: string[];
  /**
   * Legacy browser-wide "tour already shown" flag, from before the tour was
   * tracked per user. Read once and credited to a user; never set again.
   */
  hasAutoStarted: boolean;
}

interface TourActions {
  /**
   * Activate the tour from the current step. No-op once status is `completed`
   * — use `reset()` to re-run from step 0 (e.g. from the TopBar help button).
   */
  start: () => void;
  /**
   * Auto-start the intro tour on this user's first visit. No-op once they have
   * seen it, or while a tour is already running (e.g. a manual restart).
   */
  autoStart: (userId: string) => void;
  next: (totalSteps: number) => void;
  prev: () => void;
  goTo: (index: number) => void;
  skip: () => void;
  finish: () => void;
  /** Resume from a previously skipped state at the last visited step. */
  resume: () => void;
  /** Clear progress and re-run from step 0. */
  reset: () => void;
  /** A different user signed in: drop any tour in progress, keep who has seen it. */
  switchUser: (prevUserId: string | null) => void;
}

export const useTourStore = create<TourState & TourActions>()(
  devtools(
    persist(
      (set, get) => ({
        status: 'idle',
        currentStepIndex: 0,
        resetNonce: 0,
        seenUserIds: [],
        hasAutoStarted: false,

        start: () =>
          set(
            state =>
              state.status === 'completed'
                ? state
                : { status: 'active', currentStepIndex: state.currentStepIndex },
            false,
            'tour/start'
          ),

        autoStart: userId =>
          set(
            state => {
              if (state.seenUserIds.includes(userId)) return state;
              const seenUserIds = [...state.seenUserIds, userId];
              // The legacy flag can only belong to whoever is still signed in.
              if (state.hasAutoStarted || state.status !== 'idle') {
                return { seenUserIds, hasAutoStarted: false };
              }
              return { seenUserIds, status: 'active', currentStepIndex: 0 };
            },
            false,
            'tour/autoStart'
          ),

        next: totalSteps =>
          set(
            state => {
              const next = state.currentStepIndex + 1;
              if (next >= totalSteps) {
                return { status: 'completed', currentStepIndex: totalSteps - 1 };
              }
              return { currentStepIndex: next };
            },
            false,
            'tour/next'
          ),

        prev: () =>
          set(
            state => ({ currentStepIndex: Math.max(0, state.currentStepIndex - 1) }),
            false,
            'tour/prev'
          ),

        goTo: index => set({ currentStepIndex: Math.max(0, index) }, false, 'tour/goTo'),

        skip: () => set({ status: 'skipped' }, false, 'tour/skip'),

        finish: () => set({ status: 'completed' }, false, 'tour/finish'),

        resume: () => {
          const { status } = get();
          if (status === 'skipped' || status === 'idle') {
            set({ status: 'active' }, false, 'tour/resume');
          }
        },

        reset: () =>
          set(
            state => ({
              status: 'active',
              currentStepIndex: 0,
              resetNonce: state.resetNonce + 1,
            }),
            false,
            'tour/reset'
          ),

        switchUser: prevUserId =>
          set(
            state => ({
              status: 'idle',
              currentStepIndex: 0,
              hasAutoStarted: false,
              seenUserIds:
                state.hasAutoStarted && prevUserId && !state.seenUserIds.includes(prevUserId)
                  ? [...state.seenUserIds, prevUserId]
                  : state.seenUserIds,
            }),
            false,
            'tour/switchUser'
          ),
      }),
      {
        name: 'lili-tour-storage',
        partialize: state => ({
          status: state.status,
          currentStepIndex: state.currentStepIndex,
          seenUserIds: state.seenUserIds,
          hasAutoStarted: state.hasAutoStarted,
        }),
        onRehydrateStorage: () => state => {
          // The intro tour is a first-visit-only experience (LIL-355). A tour
          // left mid-flight (`active`) by a previous visit isn't resurrected —
          // only an explicit manual restart (TopBar `reset()`) re-shows it.
          if (state && state.status === 'active') {
            state.status = 'skipped';
          }
        },
      }
    ),
    {
      name: 'TourStore',
      enabled: import.meta.env.DEV,
    }
  )
);
